import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { getSaoPauloDayKey, normalizeCounterDayKey } from '@mobile/helpers/data.helper';
import * as firebase from 'firebase/app';

import { AppLogsFirebaseService } from './app-logs-firebase.service';

type AcaoContador = 'init' | 'increment' | 'reset-dia' | 'increment-data-invalida';

@Injectable({ providedIn: 'root' })
export class ContadoresFirebaseService {
  constructor(
    private afs: AngularFirestore,
    private appLogs: AppLogsFirebaseService,
  ) {}

  /** Número do pedido do dia (reinicia em 1 a cada dia de São Paulo), gerado em transação. */
  async getProximoNumeroPedido(): Promise<number> {
    const serverDate = new Date(firebase.firestore.Timestamp.now().seconds * 1000);
    const hoje = getSaoPauloDayKey(serverDate);
    const contadorRef = this.afs.collection('contadores').doc('pedidos').ref;

    try {
      const result = await this.afs.firestore.runTransaction(async (transaction) => {
        const doc = await transaction.get(contadorRef);

        if (!doc.exists) {
          transaction.set(contadorRef, { valor: 1, data: hoje });
          return {
            numero: 1,
            acao: 'init' as AcaoContador,
            valorAnterior: null as number | null,
            dataAnterior: null as string | null,
            dataNormalizada: null as string | null,
          };
        }

        const data = doc.data() as { valor: number; data: string };
        const dataNormalizada = normalizeCounterDayKey(data.data, hoje);
        let proximoNumero = Number(data.valor ?? 0) + 1;
        let acao: AcaoContador = 'increment';

        if (dataNormalizada && dataNormalizada !== hoje) {
          proximoNumero = 1;
          acao = 'reset-dia';
        } else if (!dataNormalizada) {
          console.warn('[contador-pedidos] data inválida no contador, mantendo sequência e corrigindo para hoje:', data.data);
          acao = 'increment-data-invalida';
        }

        transaction.update(contadorRef, { valor: proximoNumero, data: hoje });

        return {
          numero: proximoNumero,
          acao,
          valorAnterior: data.valor as number | null,
          dataAnterior: data.data as string | null,
          dataNormalizada,
        };
      });

      const transicao = `${result.valorAnterior ?? 0} -> ${result.numero}`;
      const dataAnteriorRaw = result.dataAnterior ?? '';
      const dataAnteriorIsIso = /^\d{4}-\d{2}-\d{2}$/.test(dataAnteriorRaw);
      const suspeitaRegressaoMesmoDia =
        result.acao === 'increment' &&
        result.dataNormalizada === hoje &&
        result.valorAnterior !== null &&
        result.valorAnterior <= 1;
      const mensagem = result.acao === 'reset-dia'
        ? `[contador-pedidos] resetando contador para 1 (${result.dataNormalizada ?? result.dataAnterior} -> ${hoje})`
        : `[contador-pedidos] ${transicao}`;

      await this.appLogs.info(mensagem, 'numero_pedido', {
        source: 'ContadoresFirebaseService.getProximoNumeroPedido',
        numeroGerado: result.numero,
        transicao,
        acao: result.acao,
        diaReferencia: hoje,
        valorAnterior: result.valorAnterior,
        dataAnterior: result.dataAnterior,
        dataNormalizada: result.dataNormalizada,
        dataAnteriorIsIso,
        suspeitaWriterLegado: !!dataAnteriorRaw && !dataAnteriorIsIso,
        suspeitaRegressaoMesmoDia,
      });

      return result.numero;
    } catch (error) {
      console.error('Erro ao gerar número do pedido:', error);
      return Math.floor(Math.random() * 1000);
    }
  }
}
